import React, { useEffect, useState } from 'react';
import Button from '../../../../../../components/controls/Button';
import { createInputFieldId, FormGroup, From, InputField } from '../../../../../../components/controls/Form';
import BaseModal from '../../../../../../components/modals/BaseModal';
import { Alert } from '../../../../../../components/visual/Alert/Alert';
import { apiPUT } from '../../../../../../utils/networking';
import useMessages from '../../../../../../utils/useMessages';

/**
 *
 * @param {{
 *  delegate?: import('../../../../../../store/reducers/delegatesReducer').TDelegateListItem;
 *  onSuccess?():void;
 * }} props
 * @returns {JSX.Element} - The rendered DelegateModal component.
 */
export const DelegateModalEditInvoker = (props) => {
  const { delegate, onSuccess } = props;
  const [isOpened, setIsOpened] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [firstName, setFirstName] = useState(delegate?.firstName ?? '');
  const [lastName, setLastName] = useState(delegate?.lastName ?? '');
  const [emailAddress, setEmailAddress] = useState(delegate?.emailAddress ?? '');
  const messages = useMessages();

  const customInputIdForFirstName = createInputFieldId()

  const getContent = () => {
    const handleFormSubmit = async () => {
      setIsBusy(true);
      setErrorMessage('');
      try {
        const payload = {
          ...delegate,
          firstName,
          lastName,
          emailAddress,
        }
        const resp = await apiPUT('/recommendation/rest/evaluatorDelegate', payload);
        if (/^20/.test(resp.status)) {
          onSuccess?.();
          onModalClose();
          messages.clear().notifyGreen('Delegate edited successfully.');
        } else {
          throw new Error('An error occurred while editing delegate info. Please try again.');
        }
      } catch (error) {
        console.error(error);
        setErrorMessage(
          'An error occurred while editing delegate info. Please try again.'
        );
      } finally {
        setIsBusy(false);
      }
    }

    // ensure the right input field is focused when the modal is opened
    setTimeout(() => {
      document.getElementById(customInputIdForFirstName)?.focus();
    }, 100);

    return <>
      <From
        noAutocomplete
        onSubmit={handleFormSubmit}
        formAttr={{
          title: 'Edit Delegate',
        }}
        noSpacings
        errors={<Alert show={!!errorMessage} message={errorMessage} type={'red'} />}
        buttons={
          <>
            <Button
              primary
              outline
              type='button'
              disabled={isBusy}
              onClick={onModalClose}
            >
              Cancel
            </Button>

            <Button
              primary
              submit
              disabled={isBusy}
              isBusy={isBusy}
              type='button'
            >
              Save & Close
            </Button>
          </>
        }
      >

        <FormGroup
          label={'First Name'}
          required
          horizontal
          labelSize={4}
          alignWithControlBaseline
          id={customInputIdForFirstName}
          controlRender={({ id }) => <InputField
            title={'First Name'}
            name={'firstName'}
            id={id}
            autoComplete={'new-password'}
            autofocus
            onChange={(n, value) => setFirstName(value)}
            value={firstName}
            limit={50}
            required
          />}
        />

        <FormGroup
          label={'Last Name'}
          required
          horizontal
          labelSize={4}
          alignWithControlBaseline
          controlRender={({ id }) => <InputField
            title={'Last Name'}
            name={'lastName'}
            id={id}
            autoComplete={'new-password'}
            onChange={(n, value) => setLastName(value)}
            value={lastName}
            limit={50}
            required
          />}
        />

        <FormGroup
          label={'Email'}
          required
          horizontal
          labelSize={4}
          alignWithControlBaseline
          controlRender={({ id }) => <InputField
            title={'Email'}
            name={'email'}
            inputType={'email'}
            id={id}
            autoComplete={'new-password'}
            value={emailAddress}
            limit={255}
            disabled
            required
          />}
        />

      </From>
    </>
  }

  const onModalClose = () => {
    setIsOpened(false);
  }

  useEffect(() => {
    setFirstName(delegate?.firstName ?? '');
    setLastName(delegate?.lastName ?? '');
    setEmailAddress(delegate?.emailAddress ?? '');
    return () => {
      onModalClose();
    }
  }, []);

  return <>
    <Button primary
      link
      onClick={() => {
        setIsOpened(true);
      }}>
      Edit
    </Button>
    <BaseModal
      initialFocus={false}
      show={isOpened}
      isBusy={isBusy}
      title={'Edit Delegate'}
      closeIcon={true}
      body={getContent()}
      onClose={() => {
        onModalClose?.();
      }}
      hideFooter
    />
  </>
}
